import { Component, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

// ──────────────────────────────────────────────────────────
// TYPES
// ──────────────────────────────────────────────────────────
export type AccountType   = 'Asset'|'Liability'|'Equity'|'Income'|'Expense';
export type PaymentMode   = 'Cash'|'Cheque'|'Online'|'UPI'|'DD'|'NEFT';
export type FeeStatus     = 'Paid'|'Partial'|'Pending'|'Overdue';
export type ExpenseStatus = 'Pending'|'Approved'|'Rejected'|'Paid';
export type JournalStatus = 'Draft'|'Posted'|'Void';
export type FinanceTab    = 'dashboard'|'fees'|'expenses'|'accounts'|'journal'|'reports';
export type FeeSubTab     = 'collection'|'structure';
export type ReportView    = 'pl'|'fee'|'expense'|'budget';

// ──────────────────────────────────────────────────────────
// INTERFACES
// ──────────────────────────────────────────────────────────
export interface FinAccount {
  id: number; code: string; name: string; type: AccountType;
  group: string; balance: number; is_active: boolean; description: string;
}
export interface FeeItem { name: string; amount: number; paid: number; waiver: number; }
export interface FeeRecord {
  id: number; receipt_no: string; student_id: string; student_name: string;
  class_name: string; section: string; roll_no: string; academic_year: string;
  fee_items: FeeItem[]; total_amount: number; paid_amount: number; balance: number;
  payment_mode: PaymentMode; payment_date: string; due_date: string;
  bank_ref: string; status: FeeStatus; remarks: string;
}
export interface FeeStructureItem {
  id: number; class_name: string; academic_year: string;
  items: { name: string; amount: number; due_date: string; }[]; total: number;
}
export interface ExpenseRecord {
  id: number; expense_no: string; category: string; description: string;
  amount: number; date: string; payment_mode: PaymentMode; vendor: string;
  invoice_no: string; approved_by: string; account_name: string;
  status: ExpenseStatus; remarks: string;
}
export interface JournalLine {
  account_id: number; account_code: string; account_name: string;
  debit: number; credit: number; narration: string;
}
export interface JournalEntry {
  id: number; entry_no: string; date: string; narration: string;
  reference: string; status: JournalStatus; lines: JournalLine[];
  total_debit: number; total_credit: number; created_by: string; created_at: string;
}
export interface BudgetItem {
  id: number; year: string; category: string; head: string; budgeted: number; actual: number;
}

// ──────────────────────────────────────────────────────────
// COMPONENT
// ──────────────────────────────────────────────────────────
@Component({
  selector: 'app-finance',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './finance.html',
  styleUrl:    './finance.scss',
})
export class Finance {

  // ── Tab state ─────────────────────────────────────────────
  activeTab   = signal<FinanceTab>('dashboard');
  feeSubTab   = signal<FeeSubTab>('collection');
  reportView  = signal<ReportView>('pl');
  toast       = signal('');

  // ── Modal state ───────────────────────────────────────────
  showFeeModal      = signal(false);
  isPaymentMode     = signal(false);
  showExpenseModal  = signal(false);
  showAccountModal  = signal(false);
  showJournalForm   = signal(false);
  viewingJournal    = signal<JournalEntry | null>(null);
  viewingFee        = signal<FeeRecord | null>(null);
  editingId         = signal<number | null>(null);

  // ── Forms ─────────────────────────────────────────────────
  feeForm:    Partial<FeeRecord> & { payment_amount?: number } = {};
  expForm:    Partial<ExpenseRecord> = {};
  accForm:    Partial<FinAccount>   = {};
  jnlForm:    { date: string; narration: string; reference: string; lines: Partial<JournalLine>[]; } =
              { date: '', narration: '', reference: '', lines: [] };
  feeSubmitted = false;
  expSubmitted = false;
  accSubmitted = false;
  jnlSubmitted = false;

  // ── Filters ───────────────────────────────────────────────
  feeSearch   = signal('');
  feeStatus   = signal<FeeStatus | ''>('');
  feeClass    = signal('');
  expSearch   = signal('');
  expCat      = signal('');
  expStatus   = signal<ExpenseStatus | ''>('');
  accType     = signal<AccountType | ''>('');
  jnlSearch   = signal('');
  jnlStatus   = signal<JournalStatus | ''>('');

  // ── Counters ──────────────────────────────────────────────
  private feeCounter = 20;
  private expCounter = 25;
  private accCounter = 35;
  private jnlCounter = 12;

  // ── Data signals ──────────────────────────────────────────
  fees         = signal<FeeRecord[]>       (this.buildFees());
  expenses     = signal<ExpenseRecord[]>   (this.buildExpenses());
  accounts     = signal<FinAccount[]>      (this.buildAccounts());
  journal      = signal<JournalEntry[]>    (this.buildJournal());
  budget       = signal<BudgetItem[]>      (this.buildBudget());
  feeStructure = signal<FeeStructureItem[]>(this.buildFeeStructure());

  // ── Computed stats ────────────────────────────────────────
  readonly feeStats = computed(() => {
    const f = this.fees();
    return {
      paid:      f.filter(x => x.status === 'Paid').length,
      partial:   f.filter(x => x.status === 'Partial').length,
      pending:   f.filter(x => x.status === 'Pending').length,
      overdue:   f.filter(x => x.status === 'Overdue').length,
      totalDue:  f.reduce((s, x) => s + x.total_amount, 0),
      totalPaid: f.reduce((s, x) => s + x.paid_amount, 0),
      totalBal:  f.reduce((s, x) => s + x.balance, 0),
    };
  });

  readonly expStats = computed(() => {
    const e = this.expenses();
    return {
      total:   e.reduce((s, x) => s + (x.status !== 'Rejected' ? x.amount : 0), 0),
      pending: e.filter(x => x.status === 'Pending').length,
      paidAmt: e.reduce((s, x) => s + (x.status === 'Paid' ? x.amount : 0), 0),
    };
  });

  readonly dashStats = computed(() => {
    const fs = this.feeStats();
    const es = this.expStats();
    return {
      revenue:     fs.totalPaid,
      expenses:    es.paidAmt,
      surplus:     fs.totalPaid - es.paidAmt,
      outstanding: fs.totalBal,
      collPct:     fs.totalDue > 0 ? Math.round((fs.totalPaid / fs.totalDue) * 100) : 0,
    };
  });

  readonly filteredFees = computed(() => {
    const q = this.feeSearch().toLowerCase();
    return this.fees()
      .filter(f =>
        (!q || [f.student_name, f.receipt_no, f.class_name, f.student_id].some(v => v.toLowerCase().includes(q))) &&
        (!this.feeStatus() || f.status === this.feeStatus()) &&
        (!this.feeClass()  || f.class_name === this.feeClass())
      )
      .sort((a, b) => b.due_date.localeCompare(a.due_date));
  });

  readonly filteredExpenses = computed(() => {
    const q = this.expSearch().toLowerCase();
    return this.expenses()
      .filter(e =>
        (!q || [e.description, e.expense_no, e.vendor, e.category].some(v => v.toLowerCase().includes(q))) &&
        (!this.expCat()    || e.category === this.expCat()) &&
        (!this.expStatus() || e.status === this.expStatus())
      )
      .sort((a, b) => b.date.localeCompare(a.date));
  });

  readonly accountsByType = computed(() => {
    const filter = this.accType();
    const acc = filter ? this.accounts().filter(a => a.type === filter) : this.accounts();
    const types: AccountType[] = ['Asset','Liability','Equity','Income','Expense'];
    return types
      .map(type => ({
        type,
        items: acc.filter(a => a.type === type && a.is_active),
        total: acc.filter(a => a.type === type && a.is_active).reduce((s, a) => s + a.balance, 0),
      }))
      .filter(g => g.items.length > 0);
  });

  readonly filteredJournal = computed(() => {
    const q = this.jnlSearch().toLowerCase();
    return this.journal()
      .filter(j =>
        (!q || [j.narration, j.entry_no, j.reference].some(v => v.toLowerCase().includes(q))) &&
        (!this.jnlStatus() || j.status === this.jnlStatus())
      )
      .sort((a, b) => b.date.localeCompare(a.date));
  });

  readonly uniqueClasses  = computed(() => [...new Set(this.fees().map(f => f.class_name))].sort());
  readonly expCategories  = computed(() => [...new Set(this.expenses().map(e => e.category))].sort());

  readonly expCategoryReport = computed(() => {
    const map: Record<string, number> = {};
    this.expenses().filter(e => e.status !== 'Rejected').forEach(e => {
      map[e.category] = (map[e.category] ?? 0) + e.amount;
    });
    const total = Object.values(map).reduce((s, v) => s + v, 0);
    return Object.entries(map)
      .map(([cat, amt]) => ({ cat, amt, pct: total > 0 ? Math.round((amt / total) * 100) : 0 }))
      .sort((a, b) => b.amt - a.amt);
  });

  readonly feeClassReport = computed(() => {
    const map: Record<string, { total: number; paid: number; balance: number; count: number }> = {};
    this.fees().forEach(f => {
      if (!map[f.class_name]) map[f.class_name] = { total: 0, paid: 0, balance: 0, count: 0 };
      map[f.class_name].total   += f.total_amount;
      map[f.class_name].paid    += f.paid_amount;
      map[f.class_name].balance += f.balance;
      map[f.class_name].count++;
    });
    return Object.entries(map)
      .map(([cls, d]) => ({ cls, ...d, pct: d.total > 0 ? Math.round((d.paid / d.total) * 100) : 0 }))
      .sort((a, b) => b.total - a.total);
  });

  readonly budgetReport = computed(() =>
    this.budget().map(b => ({
      ...b,
      variance: b.budgeted - b.actual,
      pct: b.budgeted > 0 ? Math.round((b.actual / b.budgeted) * 100) : 0,
    }))
  );

  readonly plReport = computed(() => {
    const fs = this.feeStats();
    const es = this.expStats();
    const otherIncome  = 73000;
    const totalIncome  = fs.totalPaid + otherIncome;
    const surplus      = totalIncome - es.paidAmt;
    return { feeIncome: fs.totalPaid, otherIncome, totalIncome, expTotal: es.paidAmt, surplus };
  });

  // ── Bar chart helpers ─────────────────────────────────────
  readonly monthlyData = [
    { month: 'Jan', rev: 850000,  exp: 620000 },
    { month: 'Feb', rev: 920000,  exp: 680000 },
    { month: 'Mar', rev: 780000,  exp: 590000 },
    { month: 'Apr', rev: 1100000, exp: 750000 },
    { month: 'May', rev: 960000,  exp: 720000 },
    { month: 'Jun', rev: 1050000, exp: 810000 },
  ];
  private readonly _cH   = 130;
  private readonly _cMax = 1200000;
  readonly barW = 22;

  barY(v: number): number  { return 10 + this._cH - Math.round((v / this._cMax) * this._cH); }
  barH(v: number): number  { return Math.max(2, Math.round((v / this._cMax) * this._cH)); }
  barX(i: number, isExp: boolean): number { return 50 + i * 82 + (isExp ? this.barW + 4 : 0); }
  monthLblX(i: number): number { return 50 + i * 82 + this.barW + 2; }
  readonly chartBaseY = 140;

  // Donut chart – fee status
  donutSegments(): { dash: string; offset: string; color: string; label: string; count: number; }[] {
    const fs    = this.feeStats();
    const total = fs.paid + fs.partial + fs.pending + fs.overdue;
    if (!total) return [];
    const r = 52, c = 2 * Math.PI * r;
    const segs = [
      { count: fs.paid,    color: '#059669', label: 'Paid'    },
      { count: fs.partial, color: '#d97706', label: 'Partial' },
      { count: fs.pending, color: '#3b82f6', label: 'Pending' },
      { count: fs.overdue, color: '#ef4444', label: 'Overdue' },
    ];
    let off = 0;
    return segs.map(s => {
      const pct  = s.count / total;
      const dash = (pct * c).toFixed(2);
      const gap  = (c * (1 - pct)).toFixed(2);
      const seg  = { ...s, dash: `${dash} ${gap}`, offset: (-(off * c)).toFixed(2) };
      off += pct;
      return seg;
    });
  }

  // Bar-width helper for expense category report
  expCatBarW(pct: number): string { return pct + '%'; }

  // ── Fee CRUD ──────────────────────────────────────────────
  openPaymentModal(fee: FeeRecord): void {
    this.feeForm = { ...fee, payment_amount: fee.balance };
    this.editingId.set(fee.id);
    this.isPaymentMode.set(true);
    this.feeSubmitted = false;
    this.showFeeModal.set(true);
  }

  openNewFeeForm(): void {
    const today = this.isoDate(0);
    this.feeForm = {
      academic_year: '2025-26', payment_mode: 'Cash',
      payment_date: today, due_date: today, status: 'Pending', remarks: '',
      fee_items: [
        { name: 'Tuition Fee', amount: 0, paid: 0, waiver: 0 },
        { name: 'Development Fee', amount: 0, paid: 0, waiver: 0 },
      ],
    };
    this.editingId.set(null);
    this.isPaymentMode.set(false);
    this.feeSubmitted = false;
    this.showFeeModal.set(true);
  }

  saveFeePayment(): void {
    this.feeSubmitted = true;
    const amt = this.feeForm.payment_amount ?? 0;
    const id  = this.editingId();
    if (!amt || !id) return;
    const now = this.isoDate(0);
    const rno = `RCPT-2026-${String(++this.feeCounter).padStart(4, '0')}`;
    this.fees.update(l => l.map(f => {
      if (f.id !== id) return f;
      const newPaid = Math.min(f.total_amount, f.paid_amount + amt);
      const newBal  = Math.max(0, f.total_amount - newPaid);
      const newSt: FeeStatus = newBal === 0 ? 'Paid' : newPaid > 0 ? 'Partial' : f.status;
      return { ...f, paid_amount: newPaid, balance: newBal, status: newSt,
               payment_date: now, receipt_no: rno, payment_mode: this.feeForm.payment_mode ?? f.payment_mode };
    }));
    this.showFeeModal.set(false);
    this.flash(`Payment of ${this.formatFull(amt)} recorded. Receipt: ${rno}`);
  }

  viewFeeDetail(f: FeeRecord): void { this.viewingFee.set(f); }

  // ── Expense CRUD ──────────────────────────────────────────
  openExpenseForm(exp?: ExpenseRecord): void {
    if (exp) { this.expForm = { ...exp }; this.editingId.set(exp.id); }
    else {
      this.expForm = {
        category: '', description: '', amount: 0, date: this.isoDate(0),
        payment_mode: 'Cash', vendor: '', invoice_no: '', account_name: '', status: 'Pending', remarks: '',
      };
      this.editingId.set(null);
    }
    this.expSubmitted = false;
    this.showExpenseModal.set(true);
  }

  saveExpense(): void {
    this.expSubmitted = true;
    if (!this.expForm.category?.trim() || !this.expForm.description?.trim() || !(this.expForm.amount! > 0)) return;
    const id = this.editingId();
    if (id) {
      this.expenses.update(l => l.map(e => e.id === id ? { ...e, ...this.expForm } as ExpenseRecord : e));
    } else {
      const n = ++this.expCounter;
      const ne: ExpenseRecord = {
        id: n, expense_no: `EXP-2026-${String(n).padStart(4,'0')}`,
        category: this.expForm.category!, description: this.expForm.description!,
        amount: this.expForm.amount!, date: this.expForm.date ?? this.isoDate(0),
        payment_mode: this.expForm.payment_mode ?? 'Cash',
        vendor: this.expForm.vendor ?? '', invoice_no: this.expForm.invoice_no ?? '',
        approved_by: '', account_name: this.expForm.account_name ?? '',
        status: 'Pending', remarks: this.expForm.remarks ?? '',
      };
      this.expenses.update(l => [ne, ...l]);
    }
    this.showExpenseModal.set(false);
    this.flash('Expense saved.');
  }

  deleteExpense(id: number): void {
    if (!confirm('Delete this expense?')) return;
    this.expenses.update(l => l.filter(e => e.id !== id));
    this.flash('Expense deleted.');
  }
  approveExpense(id: number): void {
    this.expenses.update(l => l.map(e => e.id === id ? { ...e, status: 'Approved', approved_by: 'Admin User' } : e));
    this.flash('Expense approved.');
  }
  rejectExpense(id: number): void {
    if (!confirm('Reject this expense?')) return;
    this.expenses.update(l => l.map(e => e.id === id ? { ...e, status: 'Rejected' } : e));
    this.flash('Expense rejected.');
  }
  markExpensePaid(id: number): void {
    this.expenses.update(l => l.map(e => e.id === id ? { ...e, status: 'Paid' } : e));
    this.flash('Marked as paid.');
  }

  // ── Account CRUD ──────────────────────────────────────────
  openAccountForm(acc?: FinAccount): void {
    if (acc) { this.accForm = { ...acc }; this.editingId.set(acc.id); }
    else { this.accForm = { code: '', name: '', type: 'Expense', group: '', balance: 0, is_active: true, description: '' }; this.editingId.set(null); }
    this.accSubmitted = false;
    this.showAccountModal.set(true);
  }

  saveAccount(): void {
    this.accSubmitted = true;
    if (!this.accForm.code?.trim() || !this.accForm.name?.trim()) return;
    const id = this.editingId();
    if (id) {
      this.accounts.update(l => l.map(a => a.id === id ? { ...a, ...this.accForm } as FinAccount : a));
    } else {
      const n = ++this.accCounter;
      const na: FinAccount = {
        id: n, code: this.accForm.code!, name: this.accForm.name!, type: this.accForm.type ?? 'Expense',
        group: this.accForm.group ?? '', balance: this.accForm.balance ?? 0, is_active: true, description: this.accForm.description ?? '',
      };
      this.accounts.update(l => [...l, na]);
    }
    this.showAccountModal.set(false);
    this.flash('Account saved.');
  }

  toggleAccount(id: number): void {
    this.accounts.update(l => l.map(a => a.id === id ? { ...a, is_active: !a.is_active } : a));
  }

  // ── Journal CRUD ──────────────────────────────────────────
  openJournalForm(): void {
    this.jnlForm = {
      date: this.isoDate(0), narration: '', reference: '',
      lines: [
        { account_id: 0, account_code: '', account_name: '', debit: 0, credit: 0, narration: '' },
        { account_id: 0, account_code: '', account_name: '', debit: 0, credit: 0, narration: '' },
      ],
    };
    this.jnlSubmitted = false;
    this.showJournalForm.set(true);
  }

  addJournalLine(): void {
    this.jnlForm.lines.push({ account_id: 0, account_code: '', account_name: '', debit: 0, credit: 0, narration: '' });
  }
  removeJournalLine(i: number): void { if (this.jnlForm.lines.length > 2) this.jnlForm.lines.splice(i, 1); }

  jnlDebitTotal():  number  { return this.jnlForm.lines.reduce((s, l) => s + (l.debit  ?? 0), 0); }
  jnlCreditTotal(): number  { return this.jnlForm.lines.reduce((s, l) => s + (l.credit ?? 0), 0); }
  jnlBalanced():    boolean { return Math.abs(this.jnlDebitTotal() - this.jnlCreditTotal()) < 0.01; }

  saveJournal(): void {
    this.jnlSubmitted = true;
    if (!this.jnlForm.narration.trim() || !this.jnlForm.date || !this.jnlBalanced()) return;
    const n = ++this.jnlCounter;
    const nj: JournalEntry = {
      id: n, entry_no: `JNL-2026-${String(n).padStart(4,'0')}`,
      date: this.jnlForm.date, narration: this.jnlForm.narration, reference: this.jnlForm.reference,
      status: 'Draft',
      lines: this.jnlForm.lines.map(l => ({
        account_id:   l.account_id   ?? 0,
        account_code: l.account_code ?? '',
        account_name: l.account_name ?? '',
        debit:  l.debit  ?? 0,
        credit: l.credit ?? 0,
        narration: l.narration ?? '',
      })),
      total_debit:  this.jnlDebitTotal(),
      total_credit: this.jnlCreditTotal(),
      created_by: 'Admin User', created_at: new Date().toISOString(),
    };
    this.journal.update(l => [nj, ...l]);
    this.showJournalForm.set(false);
    this.flash('Journal entry saved as draft.');
  }

  postJournal(id: number): void {
    this.journal.update(l => l.map(j => j.id === id ? { ...j, status: 'Posted' } : j));
    this.flash('Journal entry posted.');
  }
  voidJournal(id: number): void {
    if (!confirm('Void this entry? This cannot be undone.')) return;
    this.journal.update(l => l.map(j => j.id === id ? { ...j, status: 'Void' } : j));
    this.flash('Journal entry voided.');
  }
  viewJournalEntry(j: JournalEntry): void { this.viewingJournal.set(j); }

  // ── Helpers ───────────────────────────────────────────────
  formatCurrency(n: number): string {
    if (n >= 10000000) return '₹' + (n / 10000000).toFixed(2) + 'Cr';
    if (n >= 100000)   return '₹' + (n / 100000).toFixed(1)   + 'L';
    if (n >= 1000)     return '₹' + (n / 1000).toFixed(1)     + 'K';
    return '₹' + n.toFixed(0);
  }
  formatFull(n: number): string {
    return new Intl.NumberFormat('en-IN', { style:'currency', currency:'INR', maximumFractionDigits:0 }).format(n);
  }
  formatDate(s: string): string {
    if (!s) return '—';
    return new Date(s).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' });
  }
  formatDateTime(s: string): string {
    if (!s) return '—';
    return new Date(s).toLocaleString('en-IN', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' });
  }
  getInitials(n: string): string { return n.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase(); }
  private flash(m: string): void { this.toast.set(m); setTimeout(() => this.toast.set(''), 3000); }
  private isoDate(offsetDays: number): string {
    const d = new Date(); d.setDate(d.getDate() - offsetDays); return d.toISOString().substring(0, 10);
  }
  hasExpErrors(): boolean { return this.expSubmitted && (!this.expForm.category?.trim() || !this.expForm.description?.trim() || !(this.expForm.amount! > 0)); }

  getFeeStatusClass(s: FeeStatus): string {
    return { Paid:'st-paid', Partial:'st-partial', Pending:'st-fpending', Overdue:'st-overdue' }[s] ?? '';
  }
  getExpStatusClass(s: ExpenseStatus): string {
    return { Pending:'st-epending', Approved:'st-eapproved', Rejected:'st-erejected', Paid:'st-epaid' }[s] ?? '';
  }
  getJnlStatusClass(s: JournalStatus): string {
    return { Draft:'st-jdraft', Posted:'st-jposted', Void:'st-jvoid' }[s] ?? '';
  }
  getAccTypeBg(t: AccountType): string {
    return { Asset:'#dbeafe', Liability:'#fee2e2', Equity:'#ede9fe', Income:'#dcfce7', Expense:'#fef3c7' }[t] ?? '#f3f4f6';
  }
  getAccTypeColor(t: AccountType): string {
    return { Asset:'#1d4ed8', Liability:'#991b1b', Equity:'#5b21b6', Income:'#166534', Expense:'#92400e' }[t] ?? '#374151';
  }
  surplusClass(n: number): string { return n >= 0 ? 'pos-val' : 'neg-val'; }

  // ──────────────────────────────────────────────────────────
  // SEED DATA
  // ──────────────────────────────────────────────────────────
  private buildFees(): FeeRecord[] {
    const ay = '2025-26';
    const fi = (n: string, a: number, p: number): FeeItem => ({ name: n, amount: a, paid: p, waiver: 0 });
    const mk = (id: number, sid: string, name: string, cls: string, sec: string, roll: string, items: FeeItem[], mode: PaymentMode, pdate: string, ddate: string, bref: string): FeeRecord => {
      const total = items.reduce((s, i) => s + i.amount, 0);
      const paid  = items.reduce((s, i) => s + i.paid, 0);
      const bal   = total - paid;
      const status: FeeStatus = bal === 0 ? 'Paid' : paid > 0 ? 'Partial' : new Date() > new Date(ddate) ? 'Overdue' : 'Pending';
      return {
        id, receipt_no: paid > 0 ? `RCPT-2026-${String(id).padStart(4,'0')}` : '',
        student_id: sid, student_name: name, class_name: cls, section: sec, roll_no: roll,
        academic_year: ay, fee_items: items, total_amount: total, paid_amount: paid, balance: bal,
        payment_mode: mode, payment_date: pdate, due_date: ddate, bank_ref: bref, status, remarks: '',
      };
    };
    return [
      mk(1, 'S001','Arjun Sharma',   'Class XI – Science','A','11-A-01',[fi('Tuition Fee',15000,15000),fi('Development Fee',5000,5000),fi('Exam Fee',2000,2000)],     'Online',  this.isoDate(5),  this.isoDate(15),'TXN001'),
      mk(2, 'S002','Priya Mehta',    'Class XI – Science','A','11-A-02',[fi('Tuition Fee',15000,15000),fi('Development Fee',5000,2500),fi('Exam Fee',2000,0)],        'Cheque',  this.isoDate(10), this.isoDate(20),'CHQ001'),
      mk(3, 'S003','Rahul Kumar',    'Class IX',          'A','9-A-01', [fi('Tuition Fee',12000,0),   fi('Development Fee',4000,0),  fi('Exam Fee',1500,0)],          'Cash',    '',               this.isoDate(30),''),
      mk(4, 'S004','Sneha Patel',    'BCA',               'A','BCA-01', [fi('Tuition Fee',25000,25000),fi('Development Fee',8000,8000),fi('Exam Fee',3000,3000)],      'UPI',     this.isoDate(3),  this.isoDate(10),'UPI001'),
      mk(5, 'S005','Vikram Singh',   'Class IX',          'B','9-B-02', [fi('Tuition Fee',12000,12000),fi('Development Fee',4000,4000),fi('Exam Fee',1500,1500)],      'Online',  this.isoDate(7),  this.isoDate(15),'TXN002'),
      mk(6, 'S006','Kavita Yadav',   'Class XI – Science','A','11-A-03',[fi('Tuition Fee',15000,7500), fi('Development Fee',5000,0),  fi('Exam Fee',2000,0)],          'Cash',    this.isoDate(20), this.isoDate(25),''),
      mk(7, 'S007','Mohit Gupta',    'BCA',               'A','BCA-02', [fi('Tuition Fee',25000,0),   fi('Development Fee',8000,0),  fi('Exam Fee',3000,0)],          'Cash',    '',               this.isoDate(45),''),
      mk(8, 'S008','Anita Singh',    'Class IX',          'C','9-C-01', [fi('Tuition Fee',12000,12000),fi('Development Fee',4000,4000),fi('Exam Fee',1500,0)],         'UPI',     this.isoDate(4),  this.isoDate(10),'UPI002'),
      mk(9, 'S009','Pooja Nair',     'BCA',               'A','BCA-03', [fi('Tuition Fee',25000,25000),fi('Development Fee',8000,8000),fi('Exam Fee',3000,3000)],       'NEFT',    this.isoDate(2),  this.isoDate(10),'NEFT001'),
      mk(10,'S010','Suresh Babu',    'Class IX',          'B','9-B-12', [fi('Tuition Fee',12000,6000), fi('Development Fee',4000,4000),fi('Exam Fee',1500,0)],          'Cash',    this.isoDate(12), this.isoDate(20),''),
      mk(11,'S011','Meena Sharma',   'Class XI – Science','A','11-A-04',[fi('Tuition Fee',15000,0),   fi('Development Fee',5000,0),  fi('Exam Fee',2000,0)],           'Cash',    '',               this.isoDate(40),''),
      mk(12,'S012','Aakash Verma',   'BBA',               'A','BBA-01', [fi('Tuition Fee',20000,20000),fi('Development Fee',7000,7000),fi('Exam Fee',2500,2500)],        'Online',  this.isoDate(1),  this.isoDate(10),'TXN003'),
      mk(13,'S013','Divya Nair',     'Class XI – Science','B','11-B-01',[fi('Tuition Fee',15000,15000),fi('Development Fee',5000,5000),fi('Exam Fee',2000,2000)],        'UPI',     this.isoDate(6),  this.isoDate(15),'UPI003'),
      mk(14,'S014','Rohan Das',      'BBA',               'A','BBA-02', [fi('Tuition Fee',20000,10000),fi('Development Fee',7000,0),  fi('Exam Fee',2500,0)],            'Cheque',  this.isoDate(15), this.isoDate(20),'CHQ002'),
      mk(15,'S015','Sunita Kumari',  'Class IX',          'A','9-A-05', [fi('Tuition Fee',12000,12000),fi('Development Fee',4000,4000),fi('Exam Fee',1500,1500)],        'Cash',    this.isoDate(8),  this.isoDate(15),''),
    ];
  }

  private buildExpenses(): ExpenseRecord[] {
    const e = (id: number, cat: string, desc: string, amt: number, dOff: number, mode: PaymentMode,
               vendor: string, inv: string, by: string, acc: string, st: ExpenseStatus, rem: string): ExpenseRecord => ({
      id, expense_no: `EXP-2026-${String(id).padStart(4,'0')}`,
      category: cat, description: desc, amount: amt, date: this.isoDate(dOff),
      payment_mode: mode, vendor, invoice_no: inv, approved_by: by, account_name: acc, status: st, remarks: rem,
    });
    return [
      e(1, 'Salary',      'Teaching staff salary – June 2026',         780000, 2,  'NEFT',   'EduSmart Payroll',      'PAY-2026-06', 'Principal','Salaries – Teaching Staff',   'Paid',     ''),
      e(2, 'Salary',      'Non-teaching staff salary – June 2026',     210000, 2,  'NEFT',   'EduSmart Payroll',      'PAY-NT-06',   'Principal','Salaries – Non-Teaching',    'Paid',     ''),
      e(3, 'Utilities',   'Electricity bill – June 2026',               28500,  5,  'Online', 'MESB',                  'EB-20260605', 'Admin',    'Electrical Charges',          'Paid',     ''),
      e(4, 'Utilities',   'Water supply – May 2026',                    8200,   10, 'Cheque', 'Municipal Corp',         'WB-20260502', 'Admin',    'Water Charges',               'Paid',     ''),
      e(5, 'Maintenance', 'Computer lab repair & servicing',            15000,  7,  'Cheque', 'TechPro Services',       'TP-4521',     'Admin',    'IT & Technology',             'Approved', ''),
      e(6, 'Academics',   'Library books – new arrivals',               45000,  4,  'Cheque', 'Oxford Bookstore',       'OX-9012',     'Principal','Stationery & Printing',       'Approved', ''),
      e(7, 'Maintenance', 'Whiteboard replacement – Classrooms 1–5',   12000,  15, 'Cash',   'FurniMart',              'FM-0012',     'Admin',    'Building Maintenance',        'Paid',     ''),
      e(8, 'Academics',   'Lab chemicals & consumables – Science',      18500,  8,  'Online', 'LabChem India Pvt Ltd',  'LC-2210',     'Principal','Lab Consumables',             'Approved', ''),
      e(9, 'Sports',      'Sports equipment – annual purchase',         32000,  3,  'Cheque', 'SportZone',              'SZ-3301',     'Principal','Sports & Activities',         'Pending',  'Awaiting approval'),
      e(10,'Transport',   'Bus maintenance & fuel – June 2026',         55000,  1,  'Cash',   'Rajesh Auto Garage',     'RA-0056',     'Admin',    'Transport Maintenance',       'Pending',  ''),
      e(11,'IT',          'Annual software license renewal',            24000,  30, 'Online', 'EduSoft Technologies',   'ES-LIC-2026', 'Admin',    'IT & Technology',             'Paid',     ''),
      e(12,'Maintenance', 'Painting & civil works – Block B',           85000,  20, 'Cheque', 'BuildFast Contractors',  'BF-1122',     'Principal','Building Maintenance',        'Approved', ''),
      e(13,'Academics',   'Stationery & printing – examinations',       14000,  6,  'Cash',   'PrintMaster',            'PM-0089',     'Admin',    'Stationery & Printing',       'Paid',     ''),
      e(14,'Marketing',   'Admission brochures & printing',             22000,  25, 'Online', 'Creative Ads Ltd',       'CA-7701',     'Principal','Marketing & Admission',       'Paid',     ''),
      e(15,'Utilities',   'Internet & broadband – June 2026',           8800,   5,  'Online', 'Jio Business',           'JIO-2026-06', 'Admin',    'Electrical Charges',          'Paid',     ''),
      e(16,'Academics',   'Projector bulb replacement ×3',              9000,   12, 'Cash',   'TechPro Services',       'TP-4612',     'Admin',    'IT & Technology',             'Paid',     ''),
      e(17,'Sports',      'Annual day event expenses',                  41000,  0,  'Online', 'EventPro',               'EP-8900',     'Principal','Sports & Activities',         'Pending',  'Event next week'),
      e(18,'Salary',      'Contract staff honorarium – June 2026',      35000,  2,  'Cash',   'Contract Staff',         'CS-0612',     'Principal','Salaries – Non-Teaching',    'Paid',     ''),
      e(19,'Maintenance', 'Generator fuel & maintenance',               18500,  9,  'Cash',   'Fuel Point',             'FP-20260601', 'Admin',    'Building Maintenance',        'Approved', ''),
      e(20,'IT',          'Online learning platform subscription',      36000,  45, 'Online', 'LearnPro Online',        'LP-ANN-2026', 'Principal','IT & Technology',             'Rejected', 'Budget exceeded'),
    ];
  }

  private buildAccounts(): FinAccount[] {
    const a = (id:number,code:string,name:string,type:AccountType,group:string,bal:number,desc:string): FinAccount =>
      ({ id, code, name, type, group, balance: bal, is_active: true, description: desc });
    return [
      a(1, '1001','Cash in Hand',              'Asset',    'Current Assets',        125000,    'Petty cash'),
      a(2, '1002','Bank Account – SBI',         'Asset',    'Current Assets',       2850000,   'SBI current account'),
      a(3, '1003','Bank Account – HDFC',        'Asset',    'Current Assets',       1420000,   'HDFC savings account'),
      a(4, '1010','Fee Receivable',             'Asset',    'Current Assets',       1280000,   'Outstanding student fees'),
      a(5, '1020','Staff Advances',             'Asset',    'Current Assets',         85000,   'Advances to staff'),
      a(6, '1100','Fixed Deposit – SBI',        'Asset',    'Non-Current Assets',  5000000,   'FD @ 7.5% p.a.'),
      a(7, '1200','Land & Building',            'Asset',    'Fixed Assets',        45000000,  'School premises'),
      a(8, '1201','Furniture & Fixtures',       'Asset',    'Fixed Assets',         1200000,  ''),
      a(9, '1202','Computers & Equipment',      'Asset',    'Fixed Assets',          850000,  'IT infrastructure'),
      a(10,'1203','Vehicles',                   'Asset',    'Fixed Assets',         2200000,  'School buses'),
      a(11,'2001','Accounts Payable',           'Liability','Current Liabilities',   185000,  'Pending vendor payments'),
      a(12,'2010','Salary Payable',             'Liability','Current Liabilities',       0,   ''),
      a(13,'2020','TDS Payable',                'Liability','Current Liabilities',    42000,  ''),
      a(14,'2030','GST Payable',                'Liability','Current Liabilities',    18000,  ''),
      a(15,'2100','Term Loan – Bank',           'Liability','Long-term Liabilities',3500000,  'Construction loan'),
      a(16,'3001','Corpus Fund',                'Equity',   'Capital',             25000000,  'Founding corpus'),
      a(17,'3002','Reserve & Surplus',          'Equity',   'Reserves',             8500000,  'Accumulated reserves'),
      a(18,'4001','Tuition Fee Income',         'Income',   'Fee Income',           6850000,  ''),
      a(19,'4002','Development Fee Income',     'Income',   'Fee Income',           2100000,  ''),
      a(20,'4003','Exam Fee Income',            'Income',   'Fee Income',            850000,  ''),
      a(21,'4004','Transport Fee Income',       'Income',   'Fee Income',            480000,  ''),
      a(22,'4010','Late Fee Income',            'Income',   'Other Income',           45000,  ''),
      a(23,'4020','Miscellaneous Income',       'Income',   'Other Income',           28000,  ''),
      a(24,'5001','Salaries – Teaching Staff',  'Expense',  'Personnel',            4680000,  ''),
      a(25,'5002','Salaries – Non-Teaching',    'Expense',  'Personnel',            1260000,  ''),
      a(26,'5010','Building Maintenance',       'Expense',  'Maintenance',           185000,  ''),
      a(27,'5011','Electrical Charges',         'Expense',  'Utilities',             171000,  ''),
      a(28,'5012','Water Charges',              'Expense',  'Utilities',              49200,  ''),
      a(29,'5020','Stationery & Printing',      'Expense',  'Academics',              78000,  ''),
      a(30,'5021','Lab Consumables',            'Expense',  'Academics',              55500,  ''),
      a(31,'5030','Sports & Activities',        'Expense',  'Co-Curricular',          96000,  ''),
      a(32,'5040','IT & Technology',            'Expense',  'Technology',             97500,  ''),
      a(33,'5050','Marketing & Admission',      'Expense',  'Marketing',              44000,  ''),
      a(34,'5060','Bank Charges',               'Expense',  'Finance',                 8500,  ''),
    ];
  }

  private buildJournal(): JournalEntry[] {
    const jl = (aid:number, code:string, name:string, dr:number, cr:number): JournalLine =>
      ({ account_id:aid, account_code:code, account_name:name, debit:dr, credit:cr, narration:'' });
    const mk = (id:number, dOff:number, narr:string, ref:string, st:JournalStatus, lines:JournalLine[]): JournalEntry => {
      const td = lines.reduce((s,l) => s+l.debit,0), tc = lines.reduce((s,l) => s+l.credit,0);
      const dt = new Date(); dt.setDate(dt.getDate() - dOff);
      return { id, entry_no:`JNL-2026-${String(id).padStart(4,'0')}`, date:this.isoDate(dOff),
               narration:narr, reference:ref, status:st, lines, total_debit:td, total_credit:tc,
               created_by:'Admin User', created_at:dt.toISOString() };
    };
    return [
      mk(1, 1,  'Fee collection – June 2026 batch',       'RCPT-BATCH-JUN', 'Posted',
         [jl(2,'1002','Bank – SBI',850000,0), jl(18,'4001','Tuition Fee Income',0,650000), jl(19,'4002','Development Fee Income',0,150000), jl(20,'4003','Exam Fee Income',0,50000)]),
      mk(2, 2,  'Teaching staff salary – June 2026',      'PAY-2026-06',    'Posted',
         [jl(24,'5001','Salaries – Teaching',780000,0), jl(2,'1002','Bank – SBI',0,780000)]),
      mk(3, 2,  'Non-teaching staff salary – June 2026',  'PAY-NT-06',      'Posted',
         [jl(25,'5002','Salaries – Non-Teaching',210000,0), jl(2,'1002','Bank – SBI',0,210000)]),
      mk(4, 5,  'Electricity bill – June 2026',           'EB-20260605',    'Posted',
         [jl(27,'5011','Electrical Charges',28500,0), jl(1,'1001','Cash in Hand',0,28500)]),
      mk(5, 7,  'Computer lab repair – TechPro',          'TP-4521',        'Posted',
         [jl(32,'5040','IT & Technology',15000,0), jl(11,'2001','Accounts Payable',0,15000)]),
      mk(6, 10, 'Library books – Oxford Bookstore',       'OX-9012',        'Posted',
         [jl(29,'5020','Stationery & Printing',45000,0), jl(11,'2001','Accounts Payable',0,45000)]),
      mk(7, 0,  'FD interest income – Q1 2026',           'FD-INT-Q1',      'Draft',
         [jl(2,'1002','Bank – SBI',93750,0), jl(23,'4020','Miscellaneous Income',0,93750)]),
      mk(8, 15, 'Vendor payment – June 2026',             'PAY-VND-JUN',    'Posted',
         [jl(11,'2001','Accounts Payable',85000,0), jl(2,'1002','Bank – SBI',0,85000)]),
    ];
  }

  private buildBudget(): BudgetItem[] {
    const b = (id:number, cat:string, head:string, budget:number, actual:number): BudgetItem =>
      ({ id, year:'2026-27', category:cat, head, budgeted:budget, actual });
    return [
      b(1, 'Income',  'Tuition Fees',         9000000, 6850000),
      b(2, 'Income',  'Development Fees',     3000000, 2100000),
      b(3, 'Income',  'Exam Fees',            1200000,  850000),
      b(4, 'Income',  'Transport Fees',        700000,  480000),
      b(5, 'Expense', 'Teaching Salaries',    8000000, 4680000),
      b(6, 'Expense', 'Non-Teaching Salaries',2200000, 1260000),
      b(7, 'Expense', 'Building Maintenance',  400000,  185000),
      b(8, 'Expense', 'IT & Technology',       300000,   97500),
      b(9, 'Expense', 'Academics & Library',   500000,  133500),
      b(10,'Expense', 'Sports & Events',       250000,   96000),
    ];
  }

  private buildFeeStructure(): FeeStructureItem[] {
    const d = (m: number) => `2025-${String(m).padStart(2,'0')}-10`;
    const s = (id:number, cls:string, items:{name:string;amount:number;due_date:string}[]): FeeStructureItem =>
      ({ id, class_name:cls, academic_year:'2025-26', items, total:items.reduce((s,i)=>s+i.amount,0) });
    return [
      s(1,'Class IX',          [{name:'Tuition Fee',amount:12000,due_date:d(4)},{name:'Development Fee',amount:4000,due_date:d(4)},{name:'Exam Fee',amount:1500,due_date:d(10)},{name:'Library Fee',amount:500,due_date:d(4)}]),
      s(2,'Class XI – Science',[{name:'Tuition Fee',amount:15000,due_date:d(4)},{name:'Development Fee',amount:5000,due_date:d(4)},{name:'Exam Fee',amount:2000,due_date:d(10)},{name:'Library Fee',amount:500,due_date:d(4)},{name:'Lab Fee',amount:2000,due_date:d(4)}]),
      s(3,'BCA',               [{name:'Tuition Fee',amount:25000,due_date:d(4)},{name:'Development Fee',amount:8000,due_date:d(4)},{name:'Exam Fee',amount:3000,due_date:d(10)},{name:'Library Fee',amount:1000,due_date:d(4)},{name:'Lab Fee',amount:3000,due_date:d(4)}]),
      s(4,'BBA',               [{name:'Tuition Fee',amount:20000,due_date:d(4)},{name:'Development Fee',amount:7000,due_date:d(4)},{name:'Exam Fee',amount:2500,due_date:d(10)},{name:'Library Fee',amount:1000,due_date:d(4)}]),
    ];
  }
}
